import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, User, Zap, Smartphone, Accessibility, Mic, MapPin } from "lucide-react";
import { PrimaryCTA } from "@/components/PrimaryCTA";
import { Link } from "react-router-dom";
import voiceSearchHero from "@/assets/blog/microphone-voice-dark.jpg";

const BlogLocalSeo = () => {
  const relatedArticles = [
    { title: "Why SEO is Dead", tag: "AI", icon: Zap },
    { title: "The 0.5-Second Rule", tag: "Speed", icon: Zap },
    { title: "Stop Losing Leads on Mobile", tag: "Mobile", icon: Smartphone },
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <Helmet>
        <title>Voice Search & Local SEO | Insights for South Wales Trades</title>
        <meta name="description" content="Optimising your website for voice search and smart speakers. Local SEO strategies for plumbers, builders and trades." />
        <link rel="canonical" href="https://smartsite-lead-gen.lovable.app/blog-localseo" />
      </Helmet>
      <Navbar />
      
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4">
          <Breadcrumbs items={[
            { label: "Home", href: "/" },
            { label: "Insights", href: "/insights" },
            { label: "Voice Search SEO", href: "/blog-localseo" }
          ]} />
          
          {/* Section 1: Article Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center mb-12"
          >
            <Badge className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white border-0 px-4 py-1.5 text-sm font-medium rounded-full mb-6">
              Voice Search
            </Badge>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Hey Siri, Find a Plumber: Optimising for{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Voice Search
              </span>
            </h1>
            
            <div className="flex items-center justify-center gap-4 text-slate-400">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>By SmartSite Team</span>
              </div>
              <span>•</span>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>5 min read</span>
              </div>
            </div>
          </motion.header>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-5xl mx-auto mb-16"
          >
            <div className="aspect-video rounded-2xl overflow-hidden border border-white/10 relative">
              <img 
                src={voiceSearchHero}
                alt="Smart speaker representing voice search and local SEO optimisation"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1024px"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Heavy dark overlay for text readability */}
              <div className="absolute inset-0 bg-black/70" />
              {/* Accent glow overlays */}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(234,179,8,0.12),transparent_50%)]" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(34,211,238,0.08),transparent_50%)]" />
            </div>
          </motion.div>

          {/* Section 2: Two Column Layout */}
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-[1fr_380px] gap-8">
              
              {/* Left Column - Article Content (70%) */}
              <motion.article
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl overflow-hidden">
                  <CardContent className="p-8 md:p-12">
                    {/* Intro */}
                    <p className="text-xl text-slate-300 leading-relaxed mb-10 font-light">
                      People don't type keywords anymore; they ask questions. "Who is the best builder near me?" requires a totally different SEO strategy. If you're still optimising for "builder Cardiff," you're already behind.
                    </p>

                    {/* Subhead 1 */}
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                      From Keywords to{" "}
                      <span className="bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text text-transparent">
                        Entities
                      </span>
                    </h2>
                    <p className="text-slate-400 leading-relaxed mb-10">
                      AI assistants like Siri, Alexa, and Google Assistant don't just match keywords—they look for "Entities." An entity is a trusted, verified business that the AI recognises as legitimate. This means your business needs to be registered, verified, and structured in a way that machines understand. Without proper entity recognition, you're invisible to voice search.
                    </p>

                    {/* Subhead 2 */}
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                      Google Business Profile{" "}
                      <span className="bg-gradient-to-r from-cyan-400 to-cyan-500 bg-clip-text text-transparent">
                        Optimisation
                      </span>
                    </h2>
                    <p className="text-slate-400 leading-relaxed mb-6">
                      Your Google Business Profile is the foundation of local SEO. We optimise every element:
                    </p>
                    <ul className="text-slate-400 leading-relaxed mb-10 space-y-3">
                      <li className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                        <span><strong className="text-white">NAP Consistency</strong>: Name, Address, Phone must be identical across all platforms.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Mic className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                        <span><strong className="text-white">Q&A Optimisation</strong>: We pre-populate questions customers ask, answered in natural language.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Zap className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                        <span><strong className="text-white">Review Strategy</strong>: Fresh, genuine reviews signal trust to voice assistants.</span>
                      </li>
                    </ul>

                    {/* Subhead 3 */}
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                      Schema Markup: The{" "}
                      <span className="bg-gradient-to-r from-orange-400 to-orange-500 bg-clip-text text-transparent">
                        Map Pack Secret
                      </span>
                    </h2>
                    <p className="text-slate-400 leading-relaxed mb-10">
                      Schema Markup is code that tells Google exactly what your business is, where it operates, and what services you offer. When someone asks "Hey Siri, find a plumber in Bridgend," the AI reads your schema to decide if you're a match. We implement LocalBusiness, Service, and GeoCoordinates schema to force Google to recommend you in the local Map Pack—the three businesses shown at the top of every local search.
                    </p>

                    {/* Conclusion */}
                    <div className="border-l-4 border-yellow-500 pl-6 py-2">
                      <p className="text-lg text-white font-medium italic">
                        Voice search is the future of local discovery. Optimise for entities and schema today, or watch your competitors take the calls tomorrow.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.article>

              {/* Right Column - Sticky Sidebar (30%) */}
              <motion.aside
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="space-y-6"
              >
                <div className="lg:sticky lg:top-28 space-y-6">
                  {/* Author Bio Card */}
                  <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl overflow-hidden">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold text-lg">
                          SS
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">SmartSite Solutions</h3>
                          <p className="text-slate-400 text-sm">North Cornelly</p>
                        </div>
                      </div>
                      <p className="text-slate-400 text-sm leading-relaxed">
                        Engineering growth for South Wales trades.
                      </p>
                    </CardContent>
                  </Card>

                  {/* CTA Card */}
                  <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 backdrop-blur-xl border-yellow-500/20 rounded-2xl overflow-hidden">
                    <CardContent className="p-6 text-center">
                      <h3 className="text-xl font-bold text-white mb-3">
                        Rank Locally.
                      </h3>
                      <p className="text-slate-400 text-sm mb-6">
                        See where you stand in the Map Pack and how to dominate local voice search.
                      </p>
                      <PrimaryCTA 
                        text="Get Map Pack Audit" 
                        href="/contact" 
                        external={false}
                        fullWidth
                      />
                    </CardContent>
                  </Card>

                  {/* Services Links Card */}
                  <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl overflow-hidden">
                    <CardContent className="p-6">
                      <h3 className="text-white font-semibold mb-4">Our Services</h3>
                      <ul className="space-y-3">
                        <li>
                          <Link 
                            to="/services" 
                            className="text-slate-400 hover:text-orange-400 transition-colors flex items-center gap-2 group"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 group-hover:scale-125 transition-transform" />
                            Web Design
                          </Link>
                        </li>
                        <li>
                          <Link 
                            to="/services" 
                            className="text-slate-400 hover:text-orange-400 transition-colors flex items-center gap-2 group"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 group-hover:scale-125 transition-transform" />
                            Local SEO
                          </Link>
                        </li>
                        <li>
                          <Link 
                            to="/services" 
                            className="text-slate-400 hover:text-orange-400 transition-colors flex items-center gap-2 group"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 group-hover:scale-125 transition-transform" />
                            Care Plans
                          </Link>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </motion.aside>
            </div>
          </div>

          {/* Section 3: Related Articles */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="max-w-6xl mx-auto mt-20"
          >
            <h2 className="text-3xl font-bold text-white mb-8">
              More{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Insights
              </span>
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {relatedArticles.map((article, index) => (
                <motion.div
                  key={article.tag}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                >
                  <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl overflow-hidden hover:border-white/20 transition-all duration-300 group cursor-pointer h-full">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-slate-700 to-slate-800 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform border border-white/10">
                        <article.icon className="w-6 h-6 text-yellow-400" />
                      </div>
                      <Badge variant="outline" className="border-white/20 text-slate-400 mb-3">
                        {article.tag}
                      </Badge>
                      <h3 className="text-lg font-semibold text-white group-hover:text-orange-400 transition-colors">
                        {article.title}
                      </h3>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogLocalSeo;
